import {
  Badge,
  Button,
  Callout,
  Card,
  Flex,
  Spinner,
  Text,
} from "@radix-ui/themes";

import type { OrderFill } from "~/v3-poc/api/orderFills.schema";
import { Section } from "./OrderDetailDialogSummary.pres";
import { ORDERS_UNAVAILABLE_VALUE } from "./OrdersWidget.utils";
import { type OrderFillRow, toOrderFillRow } from "./orderFills.utils";

export interface OrderDetailDialogFillsTablePresentationProps {
  fills: OrderFill[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage?: string;
  pageNumber: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onRetry: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

const getFillTypeColor = (
  kind: OrderFillRow["executionKind"],
): "gray" | "green" | "orange" => {
  if (kind === "partial_fill") {
    return "orange";
  }
  if (kind === "fill") {
    return "green";
  }
  return "gray";
};

const FillRow = ({ row }: { row: OrderFillRow }) => (
  <Card
    size="1"
    variant="surface"
    data-testid={`order-fill-${row.key}`}
    data-fill-type={row.executionKind}
  >
    <Flex direction="column" gap="2">
      <Flex align="center" justify="between" gap="2" wrap="wrap">
        <Badge color={getFillTypeColor(row.executionKind)} variant="soft">
          {row.typeLabel}
        </Badge>
        <Text size="2" color="gray" className="atd-numeric">
          {row.timestampLabel}
        </Text>
      </Flex>
      <Flex align="center" gap="2" wrap="wrap">
        {row.sideLabel === ORDERS_UNAVAILABLE_VALUE ? (
          <Text size="2" color="gray">
            {row.sideLabel}
          </Text>
        ) : (
          <Badge color={row.sideColor} variant="soft">
            {row.sideLabel}
          </Badge>
        )}
        <Text size="2">{row.symbolLabel}</Text>
        <Text size="2" className="atd-numeric">
          {row.quantityLabel} @ {row.priceLabel}
        </Text>
      </Flex>
      <Flex align="center" gap="3" wrap="wrap">
        <Text size="1" color="gray">
          Cumulative{" "}
          <Text as="span" size="1" className="atd-numeric">
            {row.cumulativeQuantityLabel}
          </Text>
        </Text>
        <Text size="1" color="gray">
          Remaining{" "}
          <Text as="span" size="1" className="atd-numeric">
            {row.remainingQuantityLabel}
          </Text>
        </Text>
      </Flex>
    </Flex>
  </Card>
);

export const OrderDetailDialogFillsTablePresentation = ({
  fills,
  isLoading,
  isFetching,
  isError,
  errorMessage,
  pageNumber,
  hasPreviousPage,
  hasNextPage,
  onRetry,
  onPreviousPage,
  onNextPage,
}: OrderDetailDialogFillsTablePresentationProps) => {
  const rows = fills.map(toOrderFillRow);
  const showPagination =
    hasPreviousPage || hasNextPage || pageNumber > 1 || rows.length > 0;

  return (
    <Section title="Fills">
      <Text size="2" color="gray">
        Executions for this clicked order only, in chronological order. These
        are not status-transition events. Fills from other replacement versions
        are not included, and busted or corrected prior rows are excluded by the
        query.
      </Text>
      {isLoading ? (
        <Flex align="center" gap="2" role="status">
          <Spinner size="1" />
          <Text size="2" color="gray">
            Loading fills…
          </Text>
        </Flex>
      ) : isError ? (
        <Callout.Root color="red" role="alert">
          <Callout.Text>
            {errorMessage ?? "Fills could not be loaded."}
          </Callout.Text>
          <Button
            type="button"
            size="1"
            variant="soft"
            color="red"
            onClick={onRetry}
            loading={isFetching}
          >
            Retry fills
          </Button>
        </Callout.Root>
      ) : rows.length === 0 ? (
        <Text size="2" color="gray">
          No fills were returned for this order.
        </Text>
      ) : (
        <Flex direction="column" gap="2" data-testid="order-fills">
          {isFetching ? (
            <Flex align="center" gap="2" role="status">
              <Spinner size="1" />
              <Text size="2" color="gray">
                Updating fills…
              </Text>
            </Flex>
          ) : null}
          {rows.map((row) => (
            <FillRow key={row.key} row={row} />
          ))}
        </Flex>
      )}
      {showPagination && !isLoading && !isError ? (
        <Flex
          align="center"
          justify="between"
          gap="2"
          wrap="wrap"
          data-testid="order-fills-pagination"
        >
          <Text size="1" color="gray" className="atd-numeric">
            Page {pageNumber} · {rows.length}{" "}
            {rows.length === 1 ? "fill" : "fills"}
            {hasNextPage ? " · more available" : ""}
          </Text>
          <Flex align="center" gap="2">
            <Button
              type="button"
              size="1"
              variant="soft"
              color="gray"
              onClick={onPreviousPage}
              disabled={!hasPreviousPage || isFetching}
            >
              Previous
            </Button>
            <Button
              type="button"
              size="1"
              variant="soft"
              color="gray"
              onClick={onNextPage}
              disabled={!hasNextPage || isFetching}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      ) : null}
    </Section>
  );
};
